import { useCallback, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toast, AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, Button } from "@ndd/ui";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { SessionRowData } from "./session-row";
import type { ObservationNoteData } from "../../../../shared/observation-types";
import type { AlwaysAiRole } from "../../../../shared/api-types";
import type {
  MeetingNoteData,
  MeetingNoteShareRecipientOption,
} from "./meeting-note-types";
import { SessionsPanel } from "./sessions-panel";
import { ObservationsPanel } from "./observations-panel";
import { ObservationForm } from "./observation-form";
import { MeetingNotesPanel } from "./meeting-notes-panel";
import { MeetingNoteDetail } from "./meeting-note-detail";
import {
  MeetingNoteForm,
  type SupervisorOption,
} from "./meeting-note-form";

type ContentTab = "sessions" | "observations" | "meeting-notes";

/**
 * Route-aware navigation target for the meeting-note deep link. TanStack's
 * typed navigation needs a literal route id (a plain string base won't
 * typecheck), so the workspace Meetings surface passes its own route target
 * here. When omitted the card keeps the legacy `/rds/$rdId` behavior.
 */
export type MeetingNoteBaseUrl = {
  to: "/rds/$rdId/meetings";
  params: { rdId: string };
};

interface RdContentTabsProps {
  rdId: string;
  rdSlug: string;
  rdName: string;
  sessions: SessionRowData[];
  observations: ObservationNoteData[];
  meetingNotes: MeetingNoteData[];
  meetingNoteShareRecipientOptions: MeetingNoteShareRecipientOption[];
  initialMeetingNoteId?: string | null;
  viewerRole: AlwaysAiRole;
  currentUserId: string;
  currentUserName: string;
  rdSupervisorUserId: string | null;
  rdSupervisorName: string | null;
  supervisorOptions: SupervisorOption[];
  /**
   * Workspace mode: pin the card to one tab (the surface's record section)
   * and hide the tab strip. The shipped list/create/detail/share behavior
   * carries over unchanged.
   */
  fixedTab?: ContentTab;
  /**
   * Route-aware URL the meeting-note deep link lives under. Defaults to the
   * legacy `/rds/$rdId`; the workspace Meetings surface passes its own
   * sub-route (`/rds/$rdId/meetings`).
   */
  baseUrl?: MeetingNoteBaseUrl;
  /**
   * Chrome style. "card" is the legacy List Card (tinted band, tab strip,
   * collapse control, fixed-height list viewport). "surface" is the
   * full-height workspace surface: `header` and the add action share the top
   * row, and the list runs the page's full height. Requires `fixedTab`.
   */
  chrome?: "card" | "surface";
  /** Rendered left of the add action in surface chrome (the RD header strip). */
  header?: ReactNode;
  /** Optional full-width content between the surface header and active body. */
  banner?: ReactNode;
}

const TABS: { id: ContentTab; label: string }[] = [
  { id: "sessions", label: "Sessions" },
  { id: "observations", label: "Observations" },
  { id: "meeting-notes", label: "Meeting Notes" },
];

/**
 * Wraps the sessions region in a tabbed List Card: the Sessions / Observations
 * tabs live in the tinted header band, content swaps inside the same card, and
 * a single collapse control hides the active tab's content.
 *
 * The observation create/edit form replaces the list body in place, inside the
 * card (mirroring the scorecard add flow). The Sessions tab keeps its existing
 * inline add-transcript form.
 */
export function RdContentTabs({
  rdId,
  rdSlug,
  rdName,
  sessions,
  observations,
  meetingNotes,
  meetingNoteShareRecipientOptions,
  initialMeetingNoteId,
  viewerRole,
  currentUserId,
  currentUserName,
  rdSupervisorUserId,
  rdSupervisorName,
  supervisorOptions,
  fixedTab,
  baseUrl,
  chrome = "card",
  header,
  banner,
}: RdContentTabsProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const initialSelectedMeetingNoteId =
    initialMeetingNoteId &&
    meetingNotes.some((note) => note.id === initialMeetingNoteId)
      ? initialMeetingNoteId
      : null;
  const [tab, setTab] = useState<ContentTab>(
    fixedTab ?? (initialSelectedMeetingNoteId ? "meeting-notes" : "sessions"),
  );
  const [collapsed, setCollapsed] = useState(false);
  // Preserve the "auto-open the add form on an empty list" behavior for the
  // default (Sessions) tab.
  const [addOpen, setAddOpen] = useState(
    (fixedTab ?? "sessions") === "sessions" && sessions.length === 0,
  );
  const [editingNote, setEditingNote] = useState<ObservationNoteData | null>(
    null,
  );
  const [meetingAddOpen, setMeetingAddOpen] = useState(false);
  const [editingMeetingNote, setEditingMeetingNote] =
    useState<MeetingNoteData | null>(null);
  const [selectedMeetingNoteId, setSelectedMeetingNoteId] = useState<
    string | null
  >(initialSelectedMeetingNoteId);
  const [meetingFormDirty, setMeetingFormDirty] = useState(false);
  const [showMeetingDiscardDialog, setShowMeetingDiscardDialog] =
    useState(false);
  const pendingMeetingDiscardAction = useRef<(() => void) | null>(null);

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["rd", rdId] });
    void queryClient.invalidateQueries({ queryKey: ["observations", rdId] });
    void queryClient.invalidateQueries({ queryKey: ["meeting-notes", rdId] });
  }, [queryClient, rdId]);

  const requestMeetingDiscardOrRun = useCallback(
    (action: () => void) => {
      if (tab === "meeting-notes" && meetingFormDirty) {
        pendingMeetingDiscardAction.current = action;
        setShowMeetingDiscardDialog(true);
        return;
      }
      action();
    },
    [meetingFormDirty, tab],
  );

  // Route-aware meeting-note deep link: the workspace Meetings surface passes a
  // `baseUrl` targeting its own route; without it we keep the legacy
  // `/rds/$rdId` navigation semantics.
  const navigateMeetingNote = useCallback(
    (
      search: { meetingNoteId?: string },
      options: { ignoreBlocker?: boolean } = {},
    ) => {
      if (baseUrl) {
        void navigate({
          to: baseUrl.to,
          params: baseUrl.params,
          search,
          replace: true,
          resetScroll: false,
          ignoreBlocker: options.ignoreBlocker,
        });
        return;
      }
      void navigate({
        to: "/rds/$rdId",
        params: { rdId: rdSlug },
        search,
        replace: true,
        resetScroll: false,
        ignoreBlocker: options.ignoreBlocker,
      });
    },
    [baseUrl, navigate, rdSlug],
  );

  const clearMeetingNoteUrl = useCallback(
    (options: { ignoreBlocker?: boolean } = {}) => {
      navigateMeetingNote({}, options);
    },
    [navigateMeetingNote],
  );

  const handleTabChange = useCallback(
    (next: ContentTab) => {
      if (next === tab || fixedTab) return;
      requestMeetingDiscardOrRun(() => {
        setTab(next);
        setAddOpen(false);
        setEditingNote(null);
        setMeetingAddOpen(false);
        setEditingMeetingNote(null);
        setSelectedMeetingNoteId(null);
        setMeetingFormDirty(false);
        setCollapsed(false);
        if (next !== "meeting-notes") {
          clearMeetingNoteUrl({ ignoreBlocker: true });
        }
      });
    },
    [clearMeetingNoteUrl, fixedTab, requestMeetingDiscardOrRun, tab],
  );

  const handleCollapseToggle = useCallback(() => {
    if (!collapsed && tab === "meeting-notes") {
      requestMeetingDiscardOrRun(() => {
        setMeetingAddOpen(false);
        setEditingMeetingNote(null);
        setMeetingFormDirty(false);
        setCollapsed(true);
      });
      return;
    }
    setCollapsed((prev) => !prev);
  }, [collapsed, requestMeetingDiscardOrRun, tab]);

  const handleAdd = useCallback(() => {
    if (tab === "meeting-notes") {
      setCollapsed(false);
      setMeetingAddOpen(true);
      setEditingMeetingNote(null);
      setSelectedMeetingNoteId(null);
      clearMeetingNoteUrl();
      return;
    }
    setCollapsed(false);
    setEditingNote(null);
    setMeetingAddOpen(false);
    setEditingMeetingNote(null);
    setSelectedMeetingNoteId(null);
    setAddOpen(true);
  }, [clearMeetingNoteUrl, tab]);

  const handleObsEdit = useCallback((note: ObservationNoteData) => {
    setAddOpen(false);
    setEditingNote(note);
    setCollapsed(false);
  }, []);

  const closeObsForm = useCallback(() => {
    setAddOpen(false);
    setEditingNote(null);
  }, []);

  const handleMeetingEdit = useCallback((note: MeetingNoteData) => {
    setAddOpen(false);
    setEditingNote(null);
    setMeetingAddOpen(false);
    setSelectedMeetingNoteId(note.id);
    setEditingMeetingNote(note);
    setCollapsed(false);
  }, []);

  const handleMeetingSelect = useCallback((note: MeetingNoteData) => {
    setAddOpen(false);
    setEditingNote(null);
    setMeetingAddOpen(false);
    setEditingMeetingNote(null);
    setSelectedMeetingNoteId(note.id);
    setCollapsed(false);
    navigateMeetingNote({ meetingNoteId: note.id });
  }, [navigateMeetingNote]);

  const closeMeetingDetail = useCallback(() => {
    setSelectedMeetingNoteId(null);
    clearMeetingNoteUrl();
  }, [clearMeetingNoteUrl]);

  const closeMeetingForm = useCallback(() => {
    setMeetingAddOpen(false);
    setEditingMeetingNote(null);
    setMeetingFormDirty(false);
  }, []);

  // The handlers keep addOpen and editingNote mutually exclusive (opening one
  // clears the other), so only one form is ever shown.
  const obsFormOpen =
    tab === "observations" && (addOpen || editingNote !== null);
  const meetingFormOpen =
    tab === "meeting-notes" && (meetingAddOpen || editingMeetingNote !== null);

  const handleObsSuccess = useCallback(() => {
    const wasEdit = editingNote !== null;
    closeObsForm();
    toast.success(wasEdit ? "Observation updated" : "Observation added");
    refresh();
  }, [editingNote, closeObsForm, refresh]);

  const handleObsDeleted = useCallback(() => {
    toast.success("Observation deleted");
    refresh();
  }, [refresh]);

  const handleObsImagesChanged = useCallback(() => {
    refresh();
  }, [refresh]);

  const handleMeetingSuccess = useCallback(() => {
    const wasEdit = editingMeetingNote !== null;
    const editedNoteId = editingMeetingNote?.id ?? null;
    closeMeetingForm();
    if (editedNoteId) setSelectedMeetingNoteId(editedNoteId);
    toast.success(wasEdit ? "Meeting note updated" : "Meeting note added");
    refresh();
  }, [editingMeetingNote, closeMeetingForm, refresh]);

  const handleMeetingDeleted = useCallback(() => {
    setSelectedMeetingNoteId(null);
    clearMeetingNoteUrl();
    toast.success("Meeting note deleted");
    refresh();
  }, [clearMeetingNoteUrl, refresh]);

  const handleMeetingShared = useCallback(() => {
    refresh();
  }, [refresh]);

  const selectedMeetingNote = selectedMeetingNoteId
    ? meetingNotes.find((note) => note.id === selectedMeetingNoteId) ?? null
    : null;

  const count =
    tab === "sessions"
      ? sessions.length
      : tab === "observations"
        ? observations.length
        : meetingNotes.length;
  const addLabel =
    tab === "sessions"
      ? "+ Add Transcript"
      : tab === "observations"
        ? "+ Add Observation"
        : "+ Add Meeting Note";
  // Hide the band's add button while the active tab's form is open.
  const formOpen =
    tab === "sessions"
      ? addOpen
      : tab === "observations"
        ? obsFormOpen
        : meetingFormOpen;
  const tabs = TABS.filter(({ id }) => !fixedTab || id === fixedTab);
  const surface = chrome === "surface";

  const content =
    tab === "sessions" ? (
      <SessionsPanel
        sessions={sessions}
        rdId={rdId}
        rdSlug={rdSlug}
        addOpen={addOpen}
        setAddOpen={setAddOpen}
        surface={surface}
      />
    ) : tab === "meeting-notes" ? (
      meetingFormOpen ? (
        <div className="p-3">
          <MeetingNoteForm
            rdId={rdId}
            note={editingMeetingNote ?? undefined}
            viewerRole={viewerRole}
            currentUserId={currentUserId}
            currentUserName={currentUserName}
            rdSupervisorUserId={rdSupervisorUserId}
            rdSupervisorName={rdSupervisorName}
            supervisorOptions={supervisorOptions}
            onSuccess={handleMeetingSuccess}
            onClose={closeMeetingForm}
            onDirtyChange={setMeetingFormDirty}
          />
        </div>
      ) : selectedMeetingNote ? (
        <div
          className={cn(
            surface &&
              "overflow-hidden rounded-xl border border-sand-200 bg-card shadow-sm",
          )}
        >
          <MeetingNoteDetail
            rdId={rdId}
            rdSlug={rdSlug}
            rdName={rdName}
            note={selectedMeetingNote}
            recipientOptions={meetingNoteShareRecipientOptions}
            onBack={closeMeetingDetail}
            onEdit={handleMeetingEdit}
            onShared={handleMeetingShared}
            onDeleted={handleMeetingDeleted}
          />
        </div>
      ) : (
        <MeetingNotesPanel
          notes={meetingNotes}
          onAdd={handleAdd}
          onSelect={handleMeetingSelect}
          surface={surface}
        />
      )
    ) : obsFormOpen ? (
      /* Create/edit form replaces the list body, inside the card */
      <div className="p-3">
        <ObservationForm
          rdId={rdId}
          note={!addOpen && editingNote ? editingNote : undefined}
          onSuccess={handleObsSuccess}
          onImagesChanged={handleObsImagesChanged}
          onClose={closeObsForm}
        />
      </div>
    ) : (
      <ObservationsPanel
        rdId={rdId}
        rdSlug={rdSlug}
        observations={observations}
        onAdd={handleAdd}
        onEdit={handleObsEdit}
        onDeleted={handleObsDeleted}
        surface={surface}
      />
    );

  const discardDialog = (
    <AlertDialog
      open={showMeetingDiscardDialog}
      onOpenChange={setShowMeetingDiscardDialog}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
          <AlertDialogDescription>
            This meeting note has edits that haven&apos;t been saved. If you
            leave now, those changes will be lost.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep editing</AlertDialogCancel>
          <AlertDialogAction
            className="bg-error text-white hover:bg-error/90"
            onClick={() => {
              setShowMeetingDiscardDialog(false);
              const action = pendingMeetingDiscardAction.current;
              pendingMeetingDiscardAction.current = null;
              action?.();
            }}
          >
            Discard changes
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  if (surface) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <div className="min-w-0 flex-1">{header}</div>
          {!formOpen && <Button onClick={handleAdd}>{addLabel}</Button>}
        </div>
        {banner}
        {content}
        {discardDialog}
      </div>
    );
  }

  return (
    <section className="overflow-hidden rounded-xl border border-sand-200 bg-card shadow-sm">
      {/* Header band: collapse + tabs (left), count + add button (right) */}
      <div className="flex items-center justify-between gap-3 border-b border-sand-200 bg-sand-100 px-3 py-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleCollapseToggle}
            className="flex items-center text-sand-400 transition-colors hover:text-sand-500"
            aria-label={collapsed ? "Expand" : "Collapse"}
            aria-expanded={!collapsed}
          >
            {collapsed ? (
              <ChevronRight className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
          </button>
          <div className="flex gap-1">
            {tabs.map(({ id, label }) => {
              const active = tab === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => handleTabChange(id)}
                  className={cn(
                    "border-b-2 px-2 py-1 font-sans text-[12px] font-bold uppercase tracking-wider transition-colors",
                    active
                      ? "border-interactive text-bark"
                      : "border-transparent text-sand-400 hover:text-sand-500",
                  )}
                  aria-selected={active}
                  role="tab"
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs text-sand-400">{count} total</span>
          {!formOpen && (
            <Button size="sm" onClick={handleAdd}>
              {addLabel}
            </Button>
          )}
        </div>
      </div>

      {/* Active tab content */}
      {!collapsed && content}
      {discardDialog}
    </section>
  );
}
